import React, {
  useEffect, useState, useReducer, useContext,
} from 'react';
import PropTypes from 'prop-types';
import { MultiSelect, MultiSelectOption } from '@americanexpress/dls-react';
import { validateDlsInput } from '../../Validations/Validations';
import useMultiSelectDropDownOptions from '../../CustomHooks/useMultiSelectDropDownOptions';
import { MemberContext } from '../context';
import { performOperations } from '../../Operations/Operations';

const getVal = require('lodash/get');

/**
 * returns DLS select component
 * @param {*} props
 */
const MultiSelectComponent = (props) => {
  const {
    fieldLabel,
    fieldId,
    fieldValidation,
    cssProperties,
    formSubmissionPayload,
    setValidationErrors,
    validationErrors,
    setFocus,
    fieldRank,
    validations = [],
    idsToClear,
    dispatchIdsState,
    entityToEdit,
    changePageValues,
    setChangePageValues,
    isChangePageValue = false,
    operationsToPerform,
    setFormSubmissionPayload,
  } = props;
  const minimumSelections = fieldValidation && fieldValidation.minimumSelections
    ? fieldValidation.minimumSelections : 1;
  const options = useMultiSelectDropDownOptions({ ...props });
  const [selectedOptionIds, setSelectedOptionIds] = useState([]);
  const [validationErrState, dispatchErrState] = useReducer(
    (prevState, payload) => ({ ...prevState, ...payload }),
    { validationErr: '', isValid: true }
  );

  const memberContext = useContext(MemberContext);

  const handleOnChange = (id, isSelected) => {
    let newSelectedIds = [];
    const selectedOptionsValues = [];
    if (isSelected) {
      newSelectedIds = [...selectedOptionIds, id];
    } else if (!isSelected) {
      newSelectedIds = selectedOptionIds.filter((optionId) => optionId !== id);
    }

    if (newSelectedIds.length > 0) {
      newSelectedIds.forEach((optionId) => {
        const optionObj = options.find((opt) => opt.id === optionId);
        if (optionObj) {
          selectedOptionsValues.push(optionObj.value);
        }
      });
      formSubmissionPayload[`${fieldId}`] = selectedOptionsValues;
      formSubmissionPayload[`${fieldId}-selectedIds`] = newSelectedIds;
    } else {
      delete formSubmissionPayload[fieldId];
      delete formSubmissionPayload[`${fieldId}-selectedIds`];
    }
    if (validations.length > 0) {
      const invalidMsg = validateDlsInput(
        newSelectedIds.length > 0 ? id : null,
        validations
      );
      if (invalidMsg) {
        dispatchErrState({ validationErr: `${invalidMsg}`, isValid: false });
      } else {
        dispatchErrState({ validationErr: '', isValid: true });
      }
    }

    if (operationsToPerform?.onChange?.length > 0) {
      performOperations({
        operations: operationsToPerform.onChange,
        dataObject: {
          value: selectedOptionsValues,
          formSubmissionPayload,
          memberContext,
        },
        functionObject: { setFormSubmissionPayload },
      });
    }

    setSelectedOptionIds(newSelectedIds);

    if (isChangePageValue) {
      changePageValues[fieldId] = newSelectedIds.join(',');
      setChangePageValues({ ...changePageValues });
    }
  };

  const handleInvalid = (e) => {
    if (validations.length > 0) {
      e.preventDefault();
      handleOnChange(null, null);
      setFocus({ ...validationErrors, ...{ [fieldRank]: `${fieldId}` } });
    } else {
      // manually focus on this element as it is not handled by DLS
      /* eslint-disable unicorn/prefer-query-selector -- need */
      document.getElementById(`${fieldId}`).focus();
      /* eslint-enable unicorn/prefer-query-selector -- need */
    }
  };

  /**
   * Back button support
   */
  useEffect(() => {
    if (formSubmissionPayload && formSubmissionPayload[`${fieldId}-selectedIds`]) {
      setSelectedOptionIds(formSubmissionPayload[`${fieldId}-selectedIds`]);
    } else {
      setSelectedOptionIds([]);
    }
  }, [fieldId]);

  /**
   * Update global error object
   */
  useEffect(() => {
    if (validationErrState.isValid && validationErrors[fieldRank]) {
      delete validationErrors[fieldRank];
      setValidationErrors({ ...validationErrors });
    } else if (!validationErrState.isValid) {
      setValidationErrors({
        ...validationErrors,
        ...{ [fieldRank]: `${fieldId}` },
      });
    }
  }, [validationErrState.isValid]);

  useEffect(() => {
    if (getVal(idsToClear.ids, fieldId)) {
      setSelectedOptionIds([]);
      changePageValues[fieldId] = '';
      setChangePageValues({ ...changePageValues });
      delete formSubmissionPayload[fieldId];
      setFormSubmissionPayload({ ...formSubmissionPayload });
      delete idsToClear.ids[fieldId];
      dispatchIdsState({ toggleState: idsToClear.toggleState, ids: idsToClear });
    }
  }, [idsToClear.toggleState]);

  useEffect(() => {
    if (entityToEdit && entityToEdit[fieldId]) {
      const ids = entityToEdit[fieldId].split(',').map((id) => id.trim());
      setSelectedOptionIds(ids);
      if (isChangePageValue) {
        changePageValues[fieldId] = entityToEdit[fieldId];
        setChangePageValues({ ...changePageValues });
      }
    }
  }, [entityToEdit]);

  useEffect(() => {
    if (isChangePageValue && fieldId in changePageValues) {
      const ids = getVal(changePageValues, fieldId).split(',').map((id) => id.trim());
      setSelectedOptionIds(ids);
    }
  }, [changePageValues, isChangePageValue]);

  return (
    <div className={cssProperties.style}>
      {
        fieldLabel && fieldLabel.label && (
          <label
            htmlFor={fieldId}
            id={`label-${fieldId}`}
            className={fieldLabel.cssProperties.style}
          >
            {fieldLabel.label}
          </label>
        )
      }
      <MultiSelect
        aria-labelledby={fieldId}
        id={fieldId}
        fluid={fieldValidation.fluid}
        aria-live="off"
        onChange={handleOnChange}
      >
        {options?.length > 0
          && options.map((option) => (
            <MultiSelectOption
              key={option.id}
              id={option.id}
              required={
                fieldValidation.required
                && selectedOptionIds.length < minimumSelections
              }
              selected={selectedOptionIds.includes(option.id)}
              onInvalid={handleInvalid}
              value={option.value}
              label={option.label}
            />
          ))}
      </MultiSelect>
      {validationErrState.validationErr && (
        <div
          id="alert-error"
          className="alert dls-color-warning pad-1-t"
        >
          <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
          <span>{validationErrState.validationErr}</span>
        </div>
      )}
    </div>
  );
};
MultiSelectComponent.propTypes = {
  operationsToPerform: PropTypes.objectOf(PropTypes.shape),
  fieldLabel: PropTypes.objectOf(PropTypes.shape),
  fieldId: PropTypes.string,
  fieldValidation: PropTypes.objectOf(PropTypes.shape),
  cssProperties: PropTypes.objectOf(PropTypes.shape),
  formSubmissionPayload: PropTypes.objectOf(PropTypes.shape),
  setValidationErrors: PropTypes.func,
  setFormSubmissionPayload: PropTypes.func,
  validationErrors: PropTypes.objectOf(PropTypes.shape),
  validations: PropTypes.arrayOf(PropTypes.shape),
  setFocus: PropTypes.func,
  fieldRank: PropTypes.number,
  dispatchIdsState: PropTypes.func,
  idsToClear: PropTypes.objectOf(PropTypes.shape),
  entityToEdit: PropTypes.objectOf(PropTypes.shape),
  changePageValues: PropTypes.objectOf(PropTypes.shape),
  setChangePageValues: PropTypes.func,
  isChangePageValue: PropTypes.bool,
};

export default MultiSelectComponent;
